import React, { VFC, useEffect, useState } from 'react';
import { makeStyles, Skeleton, Button, Box, LottiePlayerECOMMBX } from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { useInvokeSWRService, useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { useWizardStepData } from '../../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { TransferServiceImpl } from '../../../../../fundTransfer/src/domain/Transfer/usecases';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { DealerRateExecuteResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/DealerRateExecuteResponseDTO';
import { InitiateTransferResponseBody } from '../../../../../fundTransfer/src/domain/Transfer/entities/InitiateTransferResponseDTO';
import { DealerRatesWithCalculatedTimer } from '../../../../../fundTransfer/src/domain/Transfer/entities/DealerRatesResponseDTO';
import { SkeletonTypography } from '../../../../../content-composer/src/components/Generic/Skeleton';

const useStyles = makeStyles(theme => ({
  successTransferRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  successText: {
    color: theme.palette.success.main,
  },
  errorContainer: {
    wordBreak: 'break-all',
    textOverflow: 'elipsis',
  },
}));

interface stepDataValues {
  fundTransfersService: TransferServiceImpl;
  initiateRequestDealerTransferData: InitiateTransferResponseBody;
  dealerRates: DealerRatesWithCalculatedTimer;
  nextCallback?: Function;
  onCancel: () => void;
  onBackHome: () => void;
  onRefetchDetails: () => void;
  setCurrentStep: React.Dispatch<React.SetStateAction<'initTransfer' | 'makeTransfer'>>;
}

const SuccessAcceptedDealerRate: VFC = () => {
  const [timestamp] = useState(new Date().toISOString());
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const { t } = useTranslation();
  const classes = useStyles();
  const stepData = useWizardStepData('initiateAuth') as { values: stepDataValues };
  const {
    onRefetchDetails,
    initiateRequestDealerTransferData,
    fundTransfersService,
    nextCallback,
    dealerRates,
    onCancel,
    onBackHome,
    setCurrentStep,
  } = stepData.values;

  const onCloseDialog = () => {
    onCancel();
    onRefetchDetails();
  };

  useEffect(() => {
    setCurrentStep('makeTransfer');
  }, [setCurrentStep]);

  const {
    data: dealerRateExecuteData,
    error: dealerRateExecuteDataError,
    isLoading: isLoadingDealerRateExecuteData,
  } = useInvokeSWRService<DealerRateExecuteResponseBody>(
    fundTransfersService.DealerRateExecute,
    {
      requestId: initiateRequestDealerTransferData?.requestId,
      dealerRateRefNumber: dealerRates?.dealerRefNo,
      dealerUserCode: dealerRates?.userCode,
      dealerRate: dealerRates?.currencyRate,
      nextCallback,
      meta: {
        timestamp,
      },
    },
    Boolean(initiateRequestDealerTransferData && dealerRates),
    'transferService.DealerRateExecute',
  );

  if (!dealerRateExecuteData && dealerRateExecuteDataError) {
    return (
      <Box width='100%' className={classes.errorContainer}>
        <ErrorContent
          code={dealerRateExecuteDataError.code}
          message={t('transactionDetails.AcceptFinalize.Huston.Message')}
          subtitle={t('transactionDetails.AcceptFinalize.DealerRateExecute.Error.Message')}
          onClickBtn={onBackHome}
          isFullwidth={true}
        />
      </Box>
    );
  }

  return (
    <Box className={classes.successTransferRoot}>
      <SkeletonTypography
        hasSkeleton={isLoadingDealerRateExecuteData}
        typographyProps={{ variant: isMobile ? 'h3' : 'h2', className: classes.successText, component: 'h1' }}
        skeletonProps={{ variant: 'text' }}>
        {t('fundsTransfer.SuccessStep.label')}
      </SkeletonTypography>
      <SkeletonTypography
        hasSkeleton={isLoadingDealerRateExecuteData}
        typographyProps={{ align: 'center', color: 'textSecondary' }}
        skeletonProps={{ variant: 'text' }}>
        {t('fundsTransfer.SuccessStep.subtitle', {
          transactionId: '',
        })}
      </SkeletonTypography>
      <Box
        display='flex'
        justifyContent='center'
        maxWidth={isLoadingDealerRateExecuteData ? '416px' : '100%'}
        maxHeight='416px'
        width='100%'
        height='100%'
        mt={isLoadingDealerRateExecuteData ? 3 : 0}>
        {isLoadingDealerRateExecuteData ? (
          <Skeleton width='100%' height='100%'>
            <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
          </Skeleton>
        ) : (
          <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
        )}
      </Box>
      <Box textAlign='center' maxWidth='210px' width='100%'>
        <Box mt={isMobile ? 3 : 6}>
          <Button
            color='primary'
            size='large'
            variant='contained'
            title={t('fundsTransfer.SuccessStep.Button.primary')}
            hasSkeleton={isLoadingDealerRateExecuteData}
            fullWidth
            onClick={onBackHome}>
            {t('fundsTransfer.SuccessStep.Button.primary')}
          </Button>
        </Box>
      </Box>
      <Box textAlign='center' maxWidth='210px' width='100%'>
        <Box mt={1}>
          <Button
            color='default'
            size='large'
            variant='outlined'
            title={t('transactionDetails.AcceptFinalize.CloseDialog.Button.Text')}
            hasSkeleton={isLoadingDealerRateExecuteData}
            fullWidth
            onClick={onCloseDialog}>
            {t('transactionDetails.AcceptFinalize.CloseDialog.Button.Text')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export { SuccessAcceptedDealerRate };
